/**
 * ScrollWaypointWrapper - Drop-in replacement for the jQuery Waypoints library.
 *
 * Uses scroll event listeners with requestAnimationFrame throttling to detect
 * when an element's leading edge crosses a trigger line — exactly replicating
 * the original Waypoints semantics. This avoids the fundamental limitations of
 * IntersectionObserver (which tracks rectangular overlap, not single-line crossings).
 *
 * @ticket AB-8814
 */

export interface WaypointOptions {
  /** The DOM element to observe — must be a raw HTMLElement, not a jQuery object */
  element: HTMLElement;
  /** Called when the element crosses the threshold. Receives 'down'|'up' (or 'left'|'right' for horizontal). */
  handler: (this: WaypointInstance, direction: string) => void;
  /**
   * Offset in pixels or a special string value.
   * Positive values trigger earlier (before element reaches viewport edge).
   * Negative values trigger later (after element has passed the viewport edge).
   * Special string values: 'right-in-view'
   */
  offset?: number | string;
  /** If true, observe horizontal intersection instead of vertical */
  horizontal?: boolean;
  /**
   * Scoping element for the observer root (for horizontal scrollable containers).
   * Must be a raw HTMLElement, not a jQuery object.
   */
  context?: HTMLElement;
  /** Arbitrary extra data passed through to handler via this.options */
  [key: string]: unknown;
}

export interface WaypointInstance {
  /** The observed element */
  element: HTMLElement;
  /** The full options object, including any custom properties */
  options: WaypointOptions;
  /** Stop observing this element */
  destroy: () => void;
}

/** Registry of all active waypoints for cleanup, destroy-all, and refresh */
interface ActiveEntry {
  instance: WaypointInstance;
  check: () => void;
  cleanup: () => void;
}

const activeInstances: Set<ActiveEntry> = new Set();

/**
 * Creates a Waypoint-compatible scroll observer.
 *
 * On each scroll event the element's bounding rect is checked against the
 * trigger line defined by `offset`.  The handler fires exactly once per
 * crossing — matching the original jQuery Waypoints behaviour.
 *
 * @example
 * ```js
 * import { createWaypoint, destroyAllWaypoints, refreshAllWaypoints } from '@utils/intersectionObserverWrapper';
 *
 * const wp = createWaypoint({
 *   element: document.getElementById('my-section'),
 *   handler: function(direction) {
 *     console.log('Scrolled', direction); // 'down' or 'up'
 *   },
 *   offset: 400,
 * });
 *
 * // Later:
 * wp.destroy();
 * ```
 */
export function createWaypoint(options: WaypointOptions): WaypointInstance {
  const element = options.element;
  if (!element) {
    return { element, options, destroy: () => {} } as WaypointInstance;
  }

  const horizontal = options.horizontal || false;
  const offset = options.offset;
  const root = options.context || null;
  const scrollTarget: EventTarget = root || window;

  let lastScrollPos = horizontal
    ? root
      ? root.scrollLeft
      : window.scrollX
    : window.scrollY;

  /** null = first check hasn't run yet */
  let wasPastTrigger: boolean | null = null;
  let rafId: number | null = null;

  const instance: WaypointInstance = {
    element,
    options,
    destroy: () => {},
  };

  /* ------------------------------------------------------------------ */
  /*  Core check – runs once per animation frame after a scroll event   */
  /* ------------------------------------------------------------------ */
  const checkTrigger = () => {
    const rect = element.getBoundingClientRect();

    // Determine the root rectangle (custom context or viewport)
    let rootTop: number;
    let rootLeft: number;
    let rootBottom: number;
    let rootRight: number;

    if (root) {
      const r = root.getBoundingClientRect();
      rootTop = r.top;
      rootLeft = r.left;
      rootBottom = r.bottom;
      rootRight = r.right;
    } else {
      rootTop = 0;
      rootLeft = 0;
      rootBottom = window.innerHeight;
      rootRight = window.innerWidth;
    }

    /* Decide whether the element is "past" the trigger line */
    let isPastTrigger: boolean;

    if (offset === "right-in-view") {
      // 'right-in-view': triggers when the element's trailing edge is inside the root
      isPastTrigger = horizontal
        ? rect.right <= rootRight
        : rect.bottom <= rootBottom;
    } else {
      const numOffset = typeof offset === "number" ? offset : 0;
      // Standard numeric offset: element's leading edge has crossed the line
      isPastTrigger = horizontal
        ? rect.left - rootLeft <= numOffset
        : rect.top - rootTop <= numOffset;
    }

    /* Scroll-direction calculation */
    const currentScrollPos = horizontal
      ? root
        ? root.scrollLeft
        : window.scrollX
      : window.scrollY;

    let direction: string;
    if (horizontal) {
      direction = currentScrollPos >= lastScrollPos ? "right" : "left";
    } else {
      direction = currentScrollPos >= lastScrollPos ? "down" : "up";
    }

    /* Fire only on state transitions (false→true or true→false) */
    if (wasPastTrigger !== null && isPastTrigger !== wasPastTrigger) {
      wasPastTrigger = isPastTrigger;
      if (currentScrollPos !== lastScrollPos) {
        lastScrollPos = currentScrollPos;
      }
      options.handler.call(instance, direction);
      return;
    }

    /* First check: fire if already past the trigger (like Waypoints initial eval) */
    if (wasPastTrigger === null && isPastTrigger) {
      wasPastTrigger = isPastTrigger;
      if (currentScrollPos !== lastScrollPos) {
        lastScrollPos = currentScrollPos;
      }
      options.handler.call(instance, direction);
      return;
    }

    wasPastTrigger = isPastTrigger;
    if (currentScrollPos !== lastScrollPos) {
      lastScrollPos = currentScrollPos;
    }
  };

  /* rAF-throttled scroll handler */
  const onScroll = () => {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      checkTrigger();
      rafId = null;
    });
  };

  /* Attach listeners */
  scrollTarget.addEventListener("scroll", onScroll, {
    passive: true,
  } as AddEventListenerOptions);
  window.addEventListener("resize", onScroll, {
    passive: true,
  } as AddEventListenerOptions);

  /* Initial evaluation (next frame, so DOM has settled) */
  requestAnimationFrame(() => {
    checkTrigger();
  });

  /* Cleanup helper */
  const cleanup = () => {
    scrollTarget.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onScroll);
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  const entry: ActiveEntry = { instance, check: checkTrigger, cleanup };
  activeInstances.add(entry);

  instance.destroy = () => {
    cleanup();
    activeInstances.delete(entry);
  };

  return instance;
}

/**
 * Destroy all active waypoint instances.
 * Equivalent to Waypoint.destroyAll()
 */
export function destroyAllWaypoints(): void {
  activeInstances.forEach((entry) => {
    entry.cleanup();
  });
  activeInstances.clear();
}

/**
 * Refresh all active waypoints by re-evaluating their trigger state.
 * Call after layout changes that may have moved observed elements.
 * Equivalent to Waypoint.refreshAll()
 */
export function refreshAllWaypoints(): void {
  activeInstances.forEach((entry) => {
    entry.check();
  });
}
